import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import type { GradeBand } from '../constants';
import { STANDARD_LEVEL } from '../constants';

interface ReadingLevelTranslation {
  body: string;
  body_html: string;
  id: number;
  level: string | null;
  llm_completion_id: number | null;
  locale: string;
  reading_level_id: string;
  section_id: number;
}

interface FetchResult {
  section_reading_level_translation: ReadingLevelTranslation;
}

interface TextFetchState {
  data: ReadingLevelTranslation | null;
  error: string | null;
  isLoading: boolean;
}

const fetchReadingLevelText = (
  sectionId: number, readingLevel: string, locale: string,
) => axios.get<FetchResult>(
  Routes.plato_api_get_reading_level_text_section_path(sectionId),
  { params: { locale, reading_level: readingLevel } },
).then(response => response.data.section_reading_level_translation);

const cacheKey = (sectionId: number, readingLevel: string, locale: string) => `${sectionId}:${readingLevel}:${locale}`;

const useReadingLevelText = (
  sectionId: number,
  readingLevel: string,
  locale: string,
): TextFetchState => {
  const cache = useRef<Record<string, ReadingLevelTranslation>>({});
  const [data, setData] = useState<ReadingLevelTranslation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const key = cacheKey(sectionId, readingLevel, locale);
    const cached = cache.current[key];

    if (cached) {
      setData(cached);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchReadingLevelText(sectionId, readingLevel, locale)
      .then((result) => {
        if (cancelled) return;
        cache.current[key] = result;
        setData(result);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load reading level text.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [sectionId, readingLevel, locale]);

  return { data, error, isLoading };
};

const useCompareTexts = (sectionId: number, gradeBand: GradeBand, locale: string) => {
  const standard = useReadingLevelText(sectionId, STANDARD_LEVEL, locale);
  const ai = useReadingLevelText(sectionId, gradeBand, locale);

  return {
    aiText: ai.data,
    error: standard.error || ai.error,
    isLoading: standard.isLoading || ai.isLoading,
    standardText: standard.data,
  };
};

export default useCompareTexts;
